import type { Field, FieldNode } from '@fieldia/core';
import { writeMoney } from '@fieldia/widgets';
import { CODE_LANGUAGE_CHOICES } from './code-languages';
import { kindOfField } from './kinds';
import type { DesignerWords } from './designer-words';
import { en } from './locales/en';

/**
 * What changed in a field's text, number and date settings from one version
 * of a page to the next, in words: "“Bio”: takes at most 200 characters". A
 * field's own settings are said only while it stays the same kind of data —
 * a field made another kind says so on its own. It hands back the keys of its
 * widget's settings it does not say (`unsaid`), for other words to say.
 */

interface Placed {
  field: Field;
  node: FieldNode;
}

/** The words for a setting's new value, or null where these words have none for it. */
type Words = (value: unknown, now: Placed, kind: string | null, w: DesignerWords['settingsChanges']) => string | null;

const DATES = ['date', 'datetime'];

/** "today", "30 days after today", "7 days before today", "2026-03-02". */
function dayWords(limit: string, w: DesignerWords['settingsChanges']): string {
  if (limit === 'today') return w.today;
  const counted = /^today([+-])(\d+)$/.exec(limit);
  if (!counted) return limit;
  return counted[1] === '+' ? w.daysAfter(Number(counted[2])) : w.daysBefore(Number(counted[2]));
}

/** A range in words: "from 0 to 300", "at least 0", "between today and …", "no later than …". */
function range(field: Field, w: DesignerWords['settingsChanges']): string {
  const { min, max } = field as { min?: unknown; max?: unknown };
  if (DATES.includes(field.type)) {
    const [a, b] = [min, max].map((v) => (typeof v === 'string' ? dayWords(v, w) : undefined));
    return a && b ? w.between(a, b) : a ? w.noEarlier(a) : b ? w.noLater(b) : w.anyDay;
  }
  if (min !== undefined && max !== undefined) return w.fromTo(min as number, max as number);
  return min !== undefined ? w.atLeast(min as number) : max !== undefined ? w.atMost(max as number) : w.anyNumber;
}

/** The field's own settings, by key: those said together, under the first. */
const FIELD_WORDS: Record<string, Words> = {
  size: (v, _now, _kind, w) => (v === undefined ? w.anySize : w.size(v as number)),
  'min,max': (_v, now, _kind, w) => range(now.field, w),
  digits: (v, _now, _kind, w) => (v === undefined ? w.usualDecimals : w.decimals((v as number[])[1])),
  days: (v, _now, _kind, w) => (Array.isArray(v) ? w.notOn(w.dayNames.filter((_, i) => !v.includes(i + 1))) : w.anyWeekday),
  default: (v, now, _kind, w) => (now.field.type !== 'date' ? null : v === 'today' ? w.startsToday : w.startsEmpty),
  // A text typed in several languages, by their names; of those, the languages a required answer is typed in.
  translate: (v, _now, _kind, w) => (Array.isArray(v) ? w.typedIn(v.map((tag) => w.languageName(String(tag)))) : w.oneLanguage),
  requiredIn: (v, now, _kind, w) => {
    const all = (now.field as { translate?: string[] }).translate;
    return all ? w.requiredIn((Array.isArray(v) ? v : all).map((tag) => w.languageName(String(tag)))) : null;
  },
  // A date and time on a zone's clock: its own, or the page's.
  timeZone: (v, now, _kind, w) => (now.field.type !== 'datetime' ? null : v ? w.onZone(String(v)) : w.onPageZone),
  // An amount's money look, by an example in its own currency: "written as 1,610,000 EGP".
  money: (v, now, _kind, w) => {
    if (!v || now.field.type !== 'monetary') return v ? null : w.writtenAsUsual;
    const example = writeMoney(1_610_000, now.field.currency ?? 'EGP', now.field.digits?.[1] ?? 2, 'en', { ...(v as object), per: undefined, home: undefined });
    return w.writtenAs(example.replace(/\u00a0/g, ' '));
  },
};

const ICONS = ['heart', 'thumb', 'number'];
const COLOURS = ['success', 'warning', 'danger', 'info'];

/** The widget's settings, by key. */
const OPTION_WORDS: Record<string, Words> = {
  rows: (v, _now, _kind, w) => w.rows((v as number | undefined) ?? 3),
  autoGrow: (v, _now, _kind, w) => (v === false ? w.keepsHeight : w.grows),
  prefix: (v, _now, _kind, w) => (v ? w.before(String(v)) : w.noBefore),
  suffix: (v, _now, _kind, w) => (v ? w.after(String(v)) : w.noAfter),
  startLabel: (v, _now, _kind, w) => (v ? w.atStart(String(v)) : w.noStart),
  endLabel: (v, _now, _kind, w) => (v ? w.atEnd(String(v)) : w.noEnd),
  nps: (v, _now, _kind, w) => (v ? w.nps : w.noNps),
  icon: (v, _now, _kind, w) => w.icons[ICONS.includes(v as string) ? (v as 'heart' | 'thumb' | 'number') : 'star'],
  min: (v, _now, kind, w) => (kind !== 'time' ? null : v ? w.timeFrom(String(v)) : w.anyTimeFrom),
  max: (v, _now, kind, w) => (kind === 'time' ? (v ? w.timeUntil(String(v)) : w.anyTimeUntil) : kind === 'keywords' ? (v ? w.takesAtMost(v as number) : w.takesAny) : null),
  color: (v, _now, kind, w) => (kind !== 'progress' ? null : w.colours[COLOURS.includes(v as string) ? (v as 'success' | 'warning' | 'danger' | 'info') : 'none']),
  showPercent: (v, _now, kind, w) => (kind !== 'progress' ? null : v === false ? w.noPercent : w.percent),
  separator: (v, _now, kind, w) => (kind === 'keywords' ? w.apart(String(v ?? ',')) : null),
  suggestions: (v, _now, kind, w) => (kind !== 'keywords' ? null : Array.isArray(v) ? w.suggests(v as string[]) : w.suggestsNothing),
  // The business kinds' own.
  endField: (v, _now, kind, w) => (kind === 'booking-slots' ? (v ? w.endsIn(String(v)) : w.noEndWritten) : kind !== 'date-range' ? null : v ? w.endsOn(String(v)) : w.oneDate),
  // Booking slots' own.
  schedule: (v, _now, kind, w) => (kind !== 'booking-slots' ? null : v ? w.scheduleOf(String(v)) : w.ownSchedule),
  length: (v, _now, kind, w) => (kind !== 'booking-slots' ? null : typeof v === 'number' ? w.lasts(v) : w.lastsAsSaid),
  between: (v, _now, kind, w) => (kind !== 'booking-slots' ? null : typeof v === 'number' && v > 0 ? w.keptFree(v) : w.noneKeptFree),
  dependsOn: (v, _now, kind, w) => (kind !== 'booking-slots' ? null : Array.isArray(v) && v.length ? w.askedAgain(v.map(String)) : w.askedOnce),
  startField: (v, _now, kind, w) => (kind !== 'timer' ? null : v ? w.runsFrom(String(v)) : w.runsFromNothing),
  unit: (v, _now, kind, w) => (kind !== 'timer' ? null : v === 'minutes' ? w.keptInMinutes : w.keptInHours),
  label: (v, _now, kind, w) => (kind !== 'state-dot' ? null : v ? w.wordsBeside : w.noWordsBeside),
  height: (v, _now, kind, w) => (kind !== 'pdf' && kind !== 'embed' ? null : typeof v === 'number' ? w.height(v) : w.usualHeight),
  // A value shown as a code: a QR code's error correction, its size, and its words under it.
  level: (v, _now, kind, w) => (kind !== 'qr-code' ? null : w.qrLevelOf(String(v ?? 'M'))),
  size: (v, _now, kind, w) => (kind !== 'qr-code' && kind !== 'barcode' ? null : typeof v === 'number' ? w.codeSize(v) : w.usualCodeSize),
  // Pins on a picture: its picture, the field holding it, what it shows, how many it takes.
  image: (v, _now, kind, w) => (kind !== 'pins' ? null : v ? w.pinsOnPicture(String(v)) : w.pinsNoPicture),
  imageField: (v, _now, kind, w) => (kind !== 'pins' ? null : v ? w.pinsOnPictureOf(String(v)) : w.pinsNoPicture),
  alt: (v, _now, kind, w) => (kind !== 'pins' ? null : v ? w.pictureShows(String(v)) : w.pictureUnsaid),
  maxPins: (v, _now, kind, w) => (kind !== 'pins' ? null : typeof v === 'number' ? w.pinsAtMost(v) : w.pinsAny),
  showValue: (v, _now, kind, w) => (kind === 'qr-code' ? (v ? w.codeWordsUnder : w.codeAlone) : kind === 'barcode' ? (v === false ? w.codeAlone : w.codeWordsUnder) : null),
  model: (v, _now, kind, w) => (kind !== 'distribution' ? null : w.accountsFrom(String(v ?? 'account.analytic.account'))),
  currencyField: (v, _now, kind, w) => (kind !== 'tax-totals' && kind !== 'payments' ? null : v ? w.currencyFrom(String(v)) : w.noCurrency),
  editable: (v, _now, kind, w) => (kind !== 'tax-totals' ? null : v ? w.taxTyped : w.taxShown),
  dueField: (v, _now, kind, w) => (kind !== 'payments' ? null : v ? w.amountDueFrom(String(v)) : w.noAmountDue),
  columns: (v, _now, kind, w) => (kind !== 'properties' ? null : w.propertyColumns(v === 2 ? 2 : 1)),
  add: (v, _now, kind, w) => (kind !== 'properties' ? null : v ? w.canAddProperty : w.cannotAddProperty),
  language: (v, _now, kind, w) => (kind !== 'code' && kind !== 'vscode' ? null : v ? w.writtenIn(CODE_LANGUAGE_CHOICES.find(([id]) => id === v)?.[1] ?? String(v)) : w.plainText),
  step: (v, _now, kind, w) => (kind === 'time' || kind === 'date-time' ? (v ? w.everyMinutes(v as number) : w.anyMinute) : kind === 'slider' ? w.stepsBy((v as number | undefined) ?? 1) : null),
  // Scanned with the camera, a link's record by a field of its records.
  scan: (v, _now, _kind, w) => {
    const by = v && typeof v === 'object' ? (v as { by?: unknown }).by : undefined;
    return !v ? w.notScanned : typeof by === 'string' ? w.scannedBy(by) : w.scanned;
  },
};

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export function inputChanges(name: string, was: Placed, now: Placed, words: DesignerWords = en): { lines: string[]; unsaid: string[] } {
  const w = words.settingsChanges;
  const lines: string[] = [];
  const say = (said: string | null) => said !== null && lines.push(w.line(name, said));
  const kind = kindOfField(now.field, now.node);
  const own = (p: Placed, key: string) => (p.field as Record<string, unknown>)[key];
  if (was.field.type === now.field.type) {
    for (const [keys, wordsFor] of Object.entries(FIELD_WORDS)) {
      const read = (p: Placed) => keys.split(',').map((key) => own(p, key));
      if (!same(read(was), read(now))) say(wordsFor(own(now, keys), now, kind, w));
    }
  }
  const [before, after] = [was.node.options ?? {}, now.node.options ?? {}];
  const changed = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((key) => !same(before[key], after[key]));
  const unsaid: string[] = [];
  for (const key of changed) {
    const said = OPTION_WORDS[key]?.(after[key], now, kind, w) ?? null;
    if (said === null) unsaid.push(key);
    say(said);
  }
  return { lines, unsaid };
}
